/**
 * Rải nhu cầu vật tư (theo định mức) xuống các LÔ vật tư cụ thể, cho màn tạo
 * phiếu nhập kho hàng bán (#M-16).
 *
 * Định mức chỉ nói "cần bao nhiêu cái vật tư X"; phiếu kho lại đòi trừ vào LÔ
 * nào. Trước đây khoảng trống này do người lập phiếu tự bù bằng tay, và lúc tạo
 * đơn thì máy chỉ gợi ý loại vật tư chứ không đụng lô.
 */

/** Một lô vật tư đang còn tồn. */
export interface MaterialLot {
    id: string
    packaging_material_id: string
    quantity_available: number
    /** Ngày tạo lô — dùng để lấy lô CŨ trước. */
    created_at?: string | null
}

export interface LotPick {
    packaging_lot_id: string
    quantity: number
}

/** Sai số: số lượng lưu NUMERIC(15,3), lệch nhỏ hơn nửa đơn vị cuối là nhiễu. */
const QTY_EPSILON = 0.0005

/**
 * Chọn lô cho MỘT loại vật tư: lấy lô CŨ NHẤT trước cho tới khi đủ.
 *
 * Lô cũ trước là quy tắc kho đang dùng ở chỗ khác (xem `ShortagePlan` của sổ
 * kiểm kê túi): bao bì để lâu hỏng hộp, mốc, nên phải dùng trước.
 *
 * Thiếu tồn thì trả phần lấy được và `missing` > 0 — KHÔNG im lặng điền thiếu,
 * vì người lập phiếu phải biết mà đi nhập thêm.
 */
export function pickLotsForMaterial(
    materialId: string,
    needed: number,
    lots: MaterialLot[],
): { picks: LotPick[]; missing: number } {
    if (needed <= QTY_EPSILON) return { picks: [], missing: 0 }

    const candidates = lots
        .filter(
            (l) =>
                l.packaging_material_id === materialId &&
                l.quantity_available > QTY_EPSILON,
        )
        .sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''))

    const picks: LotPick[] = []
    let left = needed
    for (const lot of candidates) {
        if (left <= QTY_EPSILON) break
        const take = Math.min(left, lot.quantity_available)
        picks.push({ packaging_lot_id: lot.id, quantity: round3(take) })
        left -= take
    }
    return { picks, missing: left > QTY_EPSILON ? round3(left) : 0 }
}

/**
 * Gộp nhu cầu của nhiều loại vật tư thành danh sách dòng vật tư cho một dòng lô
 * của phiếu.
 *
 * Mỗi lô chỉ ra MỘT dòng: bảng con có ràng buộc duy nhất (dòng phiếu, lô), hai
 * dòng cùng lô sẽ vỡ lúc lưu. Vật tư khác nhau không bao giờ dùng chung lô nên
 * gộp theo lô là an toàn.
 */
export function autofillMaterials(
    needs: Array<{ material_id: string; quantity: number }>,
    lots: MaterialLot[],
): { rows: LotPick[]; missingByMaterial: Record<string, number> } {
    const byLot = new Map<string, number>()
    const missingByMaterial: Record<string, number> = {}

    for (const need of needs) {
        const { picks, missing } = pickLotsForMaterial(
            need.material_id,
            need.quantity,
            lots,
        )
        for (const p of picks) {
            byLot.set(
                p.packaging_lot_id,
                round3((byLot.get(p.packaging_lot_id) ?? 0) + p.quantity),
            )
        }
        if (missing > 0) missingByMaterial[need.material_id] = missing
    }

    return {
        rows: Array.from(byLot, ([packaging_lot_id, quantity]) => ({
            packaging_lot_id,
            quantity,
        })),
        missingByMaterial,
    }
}

/** Cột số lượng lưu NUMERIC(15,3) nên chia xong phải làm tròn về 3 chữ số. */
function round3(v: number): number {
    return Math.round(v * 1000) / 1000
}
